import React, { createContext, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
// import i18next from 'i18next'
import '../config/i18n' // Requires a re-init of translations
import { smartT } from '../config/translationGlobal'

/*
 * Context to provide translation functions to components.
 * - t: The translation function.
 * - i18n: The i18n object.
 * @type {React.Context}
 */
const TranslationContext = createContext()
export const TranslationProvider = ({ children }) => {
	const { i18n } = useTranslation()
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_, setLang] = useState(i18n.language)

	useEffect(() => {
		const listener = (lng) => setLang(lng)
		i18n.on('languageChanged', listener)
		return () => i18n.off('languageChanged', listener)
	}, [i18n])

	return (
		<TranslationContext.Provider value={{ t: smartT, i18n }}>
			{children}
		</TranslationContext.Provider>
	)
}

TranslationProvider.propTypes = {
	children: PropTypes.node.isRequired
}
export const useSmartTranslation = () => useContext(TranslationContext)

/*
 * not used
 * Higher-order component to provide the translation functions to a component.
 * @param {React.Component} Component - The component to provide the translation functions to.
 * @returns {React.Component} The component with the translation functions.
 * @example withTranslation(MyComponent) // MyComponent will now have access to the t and i18n functions
 * @usage:
 * import { withTranslation } from './contexts/TranslationContext'
 * const MyComponent = withTranslation(({ t }) => { return <div>{t('key')}</div> })
 */
export const withTranslation = (Component) => {
	const TranslatedComponent = (props) => {
		const { t, i18n } = useContext(TranslationContext)
		return <Component {...props} t={t} i18n={i18n} />
	}

	// Set a display name for better debugging and ESLint compliance
	TranslatedComponent.displayName = `withTranslation(${
		Component.displayName || Component.name || 'Component'
	})`

	return TranslatedComponent
}

/**
 * translate() method should be used strictly in functions and not in JSX. For jsx, use the withTranslation HOC.
 * This translate() does not rerender the component when the language changes. But usefull for functions that are not rerendered.
 *
 * value can be a simple string or an array.
 * If it is an array, the first element is the key and the second is the options object.
 * @param {string|Array} value - The key to translate.
 * @param {string} fallback - The fallback value if the key is not found. Should exist in the translation files.
 * @returns {string} The translated string.
 *
 * @example translate('key') // 'translated value'
 * @example translate(['key', { option: 'value' }]) // 'translated value'
 */
export const translate = (value, fallback = '') => {
	if (typeof value === 'object') {
		return value
	}

	const translation = Array.isArray(value) ? smartT(...value) : smartT(value)
	return translation || fallback
}
